import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    FaCarCrash, FaHome, FaHeartbeat, FaMobileAlt, FaCloud, FaShieldAlt,
    FaCheckCircle, FaChevronDown, FaChevronUp, FaChevronRight, FaWifi,
    FaFileContract, FaUserShield, FaSatelliteDish, FaSync, FaChartPie, FaTabletAlt
} from 'react-icons/fa';
import heroImage from '../../assets/Why is IoT Called the Next Big Thing in Tech Industry.png';

const InsurancePage = () => {
    const [openFaq, setOpenFaq] = useState(null);

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const services = [
        { icon: FaWifi, title: 'Usage-Based Insurance (UBI)', desc: 'Dynamic pricing models based on real-time telematics data.', image: 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=800&q=80' },
        { icon: FaMobileAlt, title: 'Touchless Claims', desc: 'AI-powered image recognition for instant claim settlement.', image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800&q=80' },
        { icon: FaShieldAlt, title: 'Risk Modeling', desc: 'Predictive analytics to assess risk with 99% accuracy.', image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80' },
        { icon: FaFileContract, title: 'Smart Contracts', desc: 'Blockchain-based parametric insurance for auto-payouts.', image: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800&q=80' },
        { icon: FaHome, title: 'Connected Home', desc: 'IoT integration for leak detection and proactive property protection.', image: 'https://images.unsplash.com/photo-1558002038-10917738179d?w=800&q=80' },
        { icon: FaUserShield, title: 'Cyber Insurance', desc: 'Real-time threat monitoring as part of the coverage package.', image: 'https://images.unsplash.com/photo-1563986768494-4dee46a385bd?w=800&q=80' }
    ];

    const platforms = [
        { name: 'Guidewire', icon: FaShieldAlt, desc: 'Core P&C Platform' },
        { name: 'Duck Creek', icon: FaFileContract, desc: 'SaaS Insurance' },
        { name: 'Salesforce', icon: FaCloud, desc: 'Customer 360' },
        { name: 'MuleSoft', icon: FaSync, desc: 'API Integration' },
        { name: 'Tableau', icon: FaChartPie, desc: 'Risk Analytics' },
        { name: 'Flutter', icon: FaTabletAlt, desc: 'Agent Apps' }
    ];

    // Horizontal Flow Steps (Policy Lifecycle)
    const flowSteps = [
        { title: 'Quote', icon: FaMobileAlt, color: 'from-orange-500 to-red-500' },
        { title: 'Bind', icon: FaFileContract, color: 'from-red-500 to-pink-500' },
        { title: 'Monitor', icon: FaWifi, color: 'from-purple-500 to-indigo-500' },
        { title: 'Prevent', icon: FaShieldAlt, color: 'from-blue-500 to-cyan-500' },
        { title: 'Claim', icon: FaCarCrash, color: 'from-teal-400 to-emerald-500' },
        { title: 'Settle', icon: FaCheckCircle, color: 'from-green-500 to-lime-500' }
    ];

    const faqs = [
        { q: 'How does telematics work?', a: 'We use smartphone sensors or OBD-II dongles to track driving behavior (braking, speed, corners) and adjust premiums accordingly.' },
        { q: 'Can you automate claims?', a: 'Yes. For simple property damage (like a cracked windshield), our AI can analyze a photo and approve a payout in minutes.' },
        { q: 'Is IoT data secure?', a: 'Absolutely. We ensure all sensor data is encrypted at rest and in transit, complying with GDPR and HIPAA where applicable.' },
        { q: 'Do you work with Life/Health?', a: 'Yes, we integrate with wearables (Apple Watch, Fitbit) to create dynamic "Pay-as-you-Live" health policies.' },
        { q: 'Can you replace our legacy monolith?', a: 'We specialize in "hollowing out the core" - moving modules like Claims or Policy Admin to the cloud incrementally.' }
    ];

    const whyChooseUs = [
        { title: 'InsurTech DNA', desc: 'We think like a startup but execute like an enterprise.' },
        { title: 'Customer First', desc: 'We design for the policyholder, not just the actuary.' },
        { title: 'Data Mastery', desc: 'We turn data lakes into pricing engines.' },
        { title: 'Speed', desc: 'Launch new micro-insurance products in weeks.' },
        { title: 'Ecosystems', desc: 'We integrate with auto OEMs, smart home hubs, and health apps.' },
        { title: 'Fraud AI', desc: 'Our models detect suspicious patterns 10x faster than humans.' }
    ];

    return (
        <div className="bg-white font-sans">
            {/* Hero Section */}
            <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url('${heroImage}')` }}
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-orange-900/95 via-red-900/90 to-transparent"></div>
                </div>

                <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-12 flex items-center pt-20">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-3xl text-white"
                    >
                        <p className="text-xs font-light tracking-widest uppercase text-orange-300 mb-4">Industries &gt; Financial Services</p>
                        <h1 className="text-5xl md:text-6xl font-light mb-6 leading-tight">Next-Gen <span className="text-orange-400 font-normal">Insurance</span></h1>
                        <p className="text-lg font-light text-orange-100 leading-relaxed mb-8 max-w-2xl">
                            From "repair and replace" to "predict and prevent". Harnessing IoT, AI, and Big Data to redefine risk.
                        </p>
                        <div className="flex flex-wrap gap-4">
                            <a href="#inquiry" className="px-8 py-3 bg-orange-600 text-white font-medium rounded-sm hover:bg-orange-700 hover:shadow-lg transition-all text-sm uppercase tracking-wide">
                                Digitize Now
                            </a>
                            <a href="#services" className="px-8 py-3 border border-orange-300 text-white font-medium rounded-sm hover:bg-white hover:text-orange-900 transition-all text-sm uppercase tracking-wide">
                                View Capabilities
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Intro Stats */}
            <section className="py-24 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="max-w-4xl mx-auto text-center mb-20">
                        <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-6">The Trust Gap</h2>
                        <p className="text-lg text-slate-600 leading-relaxed">
                            Customers want transparency and speed. Legacy insurers struggle to provide either.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-4 gap-12 text-center">
                        {[
                            { label: 'Claims Speed', val: '-70%' },
                            { label: 'Fraud Detection', val: '+45%' },
                            { label: 'Retention', val: '+30%' },
                            { label: 'IoT Devices', val: 'Connected' }
                        ].map((stat, idx) => (
                            <div key={idx} className="relative p-6 group">
                                <div className="absolute inset-0 bg-white rounded-sm shadow-sm transform transition-transform group-hover:-translate-y-2 group-hover:shadow-md"></div>
                                <div className="relative z-10">
                                    <div className="text-5xl font-bold text-orange-600 mb-3">{stat.val}</div>
                                    <div className="text-sm text-slate-500 uppercase tracking-widest">{stat.label}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services Grid (Hover Cards) */}
            <section id="services" className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-4">InsurTech Solutions</h2>
                        <p className="text-lg text-slate-600">
                            Covering the entire value chain.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {services.map((service, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="group relative h-80 overflow-hidden rounded-sm bg-slate-900 cursor-pointer"
                            >
                                <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110" style={{ backgroundImage: `url(${service.image})` }}></div>
                                <div className="absolute inset-0 bg-gradient-to-t from-orange-900/90 via-black/20 to-transparent"></div>

                                <div className="absolute inset-0 p-8 flex flex-col justify-end z-10">
                                    <div className="transform transition-transform duration-500 group-hover:-translate-y-4">
                                        <service.icon className="text-4xl text-orange-400 mb-4" />
                                        <h3 className="text-2xl font-light text-white mb-2">{service.title}</h3>
                                        <div className="h-0 overflow-hidden group-hover:h-auto transition-all duration-500">
                                            <p className="text-orange-100 text-sm leading-relaxed mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">{service.desc}</p>
                                        </div>
                                    </div>
                                    <div className="w-12 h-1 bg-orange-500 mt-4 transform origin-left transition-all duration-500 group-hover:w-full"></div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Tech Stack */}
            <section className="py-20 bg-slate-50 border-y border-slate-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <p className="text-center text-sm font-bold text-slate-400 uppercase tracking-widest mb-10">Ecosystem Partners</p>
                    <div className="flex flex-wrap justify-center gap-8 md:gap-16 opacity-70 grayscale hover:grayscale-0 transition-all duration-500">
                        {platforms.map((tech, idx) => (
                            <div key={idx} className="flex flex-col items-center gap-2 group cursor-default">
                                <tech.icon className="text-4xl text-slate-600 group-hover:text-orange-600 transition-colors" />
                                <span className="text-sm font-medium text-slate-500 group-hover:text-slate-800">{tech.name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Timeline (Implementation Process) */}
            <section className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-20">
                        <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-4">Policy Lifecycle</h2>
                        <p className="text-slate-600">Automated from quote to claim.</p>
                    </div>

                    <div className="relative">
                        {/* Base Line (Grey) */}
                        <div className="hidden md:block absolute top-[40px] left-0 w-full h-0.5 bg-slate-200 -z-0"></div>

                        {/* Steps Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-y-12 gap-x-4 relative">
                            {flowSteps.map((step, idx) => (
                                <div key={idx} className="relative flex flex-col items-center group">
                                    {/* Circle Icon */}
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.5 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: idx * 0.1, type: "spring", stiffness: 100 }}
                                        className="relative z-20 mb-6"
                                    >
                                        <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center text-white text-2xl shadow-xl group-hover:scale-110 transition-transform duration-300 ring-4 ring-white`}>
                                            <step.icon />
                                        </div>
                                    </motion.div>

                                    {/* Title */}
                                    <h3 className="text-lg font-bold text-slate-800 text-center">{step.title}</h3>

                                    {/* Mobile Connector */}
                                    {idx < flowSteps.length - 1 && (
                                        <div className="md:hidden absolute -bottom-8 left-1/2 w-0.5 h-8 bg-slate-200"></div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="py-24 bg-orange-900 text-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid md:grid-cols-2 gap-16 items-center">
                        <div>
                            <p className="text-orange-400 font-bold tracking-widest uppercase mb-4">Why Tech-Inovant</p>
                            <h2 className="text-4xl md:text-5xl font-light mb-8 leading-tight">Risk <br />Redefined</h2>
                            <p className="text-orange-100 text-lg leading-relaxed mb-8">
                                We help insurers stop being just payer and start being a partner in risk prevention.
                            </p>
                            <a href="#inquiry" className="inline-flex items-center text-orange-400 font-medium hover:text-white transition-colors">
                                Launch InsurTech <FaChevronRight className="ml-2 text-xs" />
                            </a>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-6">
                            {whyChooseUs.map((item, idx) => (
                                <div key={idx} className="bg-orange-800/50 p-6 rounded-sm border border-orange-700 hover:border-red-500/50 transition-colors">
                                    <FaCheckCircle className="text-red-500 text-xl mb-3" />
                                    <h3 className="text-lg font-medium mb-2">{item.title}</h3>
                                    <p className="text-orange-200 text-sm leading-relaxed">{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ & Inquiry Form */}
            <section id="inquiry" className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid lg:grid-cols-2 gap-16">

                        {/* FAQs */}
                        <div>
                            <h2 className="text-3xl font-light text-slate-900 mb-8">InsurTech FAQs</h2>
                            <div className="space-y-4">
                                {faqs.map((faq, idx) => (
                                    <div key={idx} className="border-b border-slate-200">
                                        <button
                                            onClick={() => toggleFaq(idx)}
                                            className="w-full py-6 text-left flex justify-between items-center group"
                                        >
                                            <span className="text-lg text-slate-800 font-light group-hover:text-orange-600 transition-colors">{faq.q}</span>
                                            {openFaq === idx ? <FaChevronUp className="text-orange-500" /> : <FaChevronDown className="text-slate-300" />}
                                        </button>
                                        <motion.div
                                            initial={false}
                                            animate={{ height: openFaq === idx ? 'auto' : 0, opacity: openFaq === idx ? 1 : 0 }}
                                            className="overflow-hidden"
                                        >
                                            <p className="pb-6 text-slate-500 leading-relaxed">{faq.a}</p>
                                        </motion.div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Inquiry Form */}
                        <div className="bg-slate-50 p-10 rounded-sm border border-slate-200">
                            <h2 className="text-3xl font-light text-slate-900 mb-2">Modernize Coverage</h2>
                            <p className="text-slate-500 mb-8">Build products customers actually want.</p>

                            <form className="space-y-6">
                                <div className="grid grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Name</label>
                                        <input type="text" className="w-full bg-white px-4 py-3 border-b-2 border-slate-200 focus:border-orange-500 outline-none transition-colors" placeholder="Jane Doe" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Company</label>
                                        <input type="text" className="w-full bg-white px-4 py-3 border-b-2 border-slate-200 focus:border-orange-500 outline-none transition-colors" placeholder="InsureCo" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Email</label>
                                    <input type="email" className="w-full bg-white px-4 py-3 border-b-2 border-slate-200 focus:border-orange-500 outline-none transition-colors" placeholder="jane@insureco.com" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Interest</label>
                                    <select className="w-full bg-white px-4 py-3 border-b-2 border-slate-200 focus:border-orange-500 outline-none transition-colors text-slate-700">
                                        <option>Usage Based Insurance</option>
                                        <option>Claims Automation</option>
                                        <option>Legacy Migration</option>
                                        <option>Digital Distribution</option>
                                    </select>
                                </div>

                                <button type="submit" className="w-full bg-orange-600 text-white font-bold py-4 rounded-sm hover:bg-orange-700 transition-all shadow-lg mt-4 uppercase tracking-widest text-sm">
                                    Get Risk Assessment
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default InsurancePage;
